import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { TrashIcon, WarningIcon } from '../components/icons'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { deleteProject, type Project } from '../lib/conversations'

/**
 * Proje silme penceresi - IKI secenekli.
 *
 * ## Neden tek bir "sil" yok
 *
 * "Projeyi sil" iki farkli niyeti tasiyabiliyor: ya yalnizca basligi
 * kaldirmak (sohbetler duruyor, projesiz listeye duşuyor) ya da projeyi
 * icindekilerle birlikte tamamen temizlemek. Birini varsayilan secmek,
 * kullanicinin o anda hangisini istedigini TAHMIN etmek olurdu - ve yanlis
 * tahminin bedeli ya kaybolan sohbetler ya da temizlenmeyen bir yigin
 * (kullanici karari 26.09.2026).
 *
 * ## Yikici secenek IKINCI
 *
 * `useFocusTrap` ilk odaklanabilir ogeye odakliyor ve DOM sirasi
 * "sohbetler kalsin" ile basliyor: refleksle basilan Enter hicbir sohbeti
 * silmiyor.
 */
export function DeleteProjectDialog({
  project,
  onClose,
  onDeleted,
}: {
  project: Project
  onClose(): void
  /** Silme bitti: cagiran listeyi ve acik projeyi tazeliyor. */
  onDeleted(removedConversations: number): void
}) {
  const { t } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useFocusTrap(container, { active: true, onClose })

  async function submit(withConversations: boolean) {
    setBusy(true)
    setError(null)
    try {
      const result = await deleteProject(project.id, withConversations)
      onDeleted(result.deleted_conversations)
    } catch {
      setError(t('chat.sidebar.projectDeleteFailed'))
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="confirm-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="confirm-dialog confirm-danger"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-project-title"
        ref={container}
        tabIndex={-1}
      >
        <div className="confirm-body">
          <span className="confirm-icon" aria-hidden="true">
            <WarningIcon size={20} />
          </span>
          <div className="confirm-text">
            <h2 id="delete-project-title">{t('chat.sidebar.projectDeleteTitle')}</h2>
            <p>
              {t('chat.sidebar.projectDeleteBody', {
                name: project.name,
                count: project.conversation_count,
              })}
            </p>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="confirm-actions">
          <button type="button" className="confirm-btn" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          {/* Koruyucu secenek ONCE: hicbir sohbet kaybolmuyor. */}
          <button
            type="button"
            className="confirm-btn"
            onClick={() => void submit(false)}
            disabled={busy}
          >
            {t('chat.sidebar.projectDeleteKeepChats')}
          </button>
          <button
            type="button"
            className="confirm-btn confirm-btn-primary"
            onClick={() => void submit(true)}
            disabled={busy}
          >
            <TrashIcon size={15} />
            {t('chat.sidebar.projectDeleteWithChats', { count: project.conversation_count })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
